import { io, Socket } from 'socket.io-client';
import {
  REALTIME_PROTOCOL_VERSION,
  REALTIME_SOCKET_PATH,
  RealtimeHandshakeAuth,
} from 'fym-shared/src/music24/realtimeEvents';
import { config } from '../../config/config';

/**
 * Where the socket connects. The web app calls music24-api through the same-origin prefix
 * `/api_api/` (Traefik in production and staging, `setupProxy.js` locally), so the socket path is
 * `/api_api/realtime/` on the page's own origin. That keeps `connect-src 'self'` enough and sends
 * the Cloudflare Access cookie on staging. An absolute API address gives its origin and path.
 */
export function realtimeSocketAddress(apiUrl: string = config.fymApiURL(true)): { origin?: string; path: string } {
  const socketPath = REALTIME_SOCKET_PATH.replace(/^\//, '');
  const absolute = /^(https?:\/\/[^/]+)(\/.*)?$/.exec(apiUrl);
  if (absolute !== null) {
    const [, origin, pathname = ''] = absolute;
    return { origin, path: `${pathname.replace(/\/$/, '')}/${socketPath}` };
  }
  return { path: `${apiUrl.replace(/\/$/, '')}/${socketPath}` };
}

/**
 * Opens the realtime socket. `getToken` is asked at each connection attempt, so a reconnect
 * after a network break sends the token the app holds at that time.
 *
 * WebSocket first, HTTP long-polling as the fallback for a network that blocks WebSockets.
 * `tryAllTransports` makes the client try polling when the WebSocket fails; without it, Socket.IO
 * tries only the first transport of the list. Production has two API tasks, and each polling
 * request must reach the task that holds the session: Traefik sets the sticky cookie
 * `music24_lb` for that. The page origin is the socket origin, so the browser sends the cookie
 * by itself; `withCredentials` also sends it when the API address is absolute (another origin).
 *
 * Socket.IO connects again by itself after a network break, with a growing delay. It does not
 * after the server refused the handshake or closed the socket; `useRealtimeSocket` does that.
 */
export function createRealtimeSocket(getToken: () => string): Socket {
  const { origin, path } = realtimeSocketAddress();
  const options = {
    path,
    transports: ['websocket', 'polling'],
    tryAllTransports: true,
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
    auth: (send: (auth: RealtimeHandshakeAuth) => void) => send({ token: getToken(), v: REALTIME_PROTOCOL_VERSION }),
  };
  return origin === undefined ? io(options) : io(origin, options);
}
